import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Loading from '@/components/common/Loading'

function Unsubscribe() {
  const [searchParams] = useSearchParams()
  const [teamName, setTeamName] = useState('')
  const [loading, setLoading] = useState(true)
  const [unsubscribing, setUnsubscribing] = useState(false)
  const [unsubscribed, setUnsubscribed] = useState(false)
  const [error, setError] = useState(null)

  const teamId = searchParams.get('team')
  const parentId = searchParams.get('parent')

  useEffect(() => {
    if (!teamId) {
      setError('Invalid unsubscribe link')
      setLoading(false)
      return
    }

    fetchTeamName()
  }, [teamId])

  const fetchTeamName = async () => {
    try {
      const { data, error: teamError } = await supabase
        .from('teams')
        .select('name')
        .eq('id', teamId)
        .single()

      if (teamError) throw teamError

      setTeamName(data.name)
    } catch (err) {
      console.error('Error fetching team:', err)
      setError('Team not found')
    } finally {
      setLoading(false)
    }
  }

  const handleUnsubscribe = async () => {
    setUnsubscribing(true)
    setError(null)

    try {
      let query = supabase
        .from('parents')
        .update({ opted_out_at: new Date().toISOString() })
        .eq('team_id', teamId)

      // If parent ID is provided, only unsubscribe that specific parent
      if (parentId) {
        query = query.eq('id', parentId)
      }

      const { error: updateError } = await query

      if (updateError) throw updateError

      setUnsubscribed(true)
    } catch (err) {
      console.error('Error unsubscribing:', err)
      setError(err.message || 'Failed to unsubscribe. Please try again.')
    } finally {
      setUnsubscribing(false)
    }
  }

  if (loading) {
    return <Loading text="Loading..." />
  }

  if (error && !teamName) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center py-12">
        <Container>
          <Card className="max-w-md mx-auto text-center">
            <div className="mb-4">
              <svg className="w-16 h-16 text-red-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Invalid Link</h1>
            <p className="text-brand-slate mb-6">
              This unsubscribe link is invalid or has expired.
            </p>
            <a href="/" className="text-brand-green hover:underline">
              Go to Bench Bulletin Home →
            </a>
          </Card>
        </Container>
      </div>
    )
  }

  if (unsubscribed) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center py-12">
        <Container>
          <Card className="max-w-md mx-auto text-center">
            <div className="mb-4">
              <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">
              You're Unsubscribed
            </h1>
            <p className="text-brand-slate mb-6">
              You will no longer receive emails from <strong>{teamName}</strong>.
            </p>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-6">
              <p className="text-sm text-blue-900">
                Changed your mind? Contact your team manager to be added back to the list.
              </p>
            </div>
            <a href="/" className="text-brand-green hover:underline">
              Learn more about Bench Bulletin →
            </a>
          </Card>
        </Container>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center py-12">
      <Container>
        <Card className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">
              Unsubscribe from {teamName}?
            </h1>
            <p className="text-brand-slate">
              You will stop receiving email updates from this team.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-3">
            <Button
              variant="primary"
              onClick={handleUnsubscribe}
              disabled={unsubscribing}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
            >
              {unsubscribing ? 'Unsubscribing...' : 'Yes, Unsubscribe'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => window.close()}
              className="w-full"
            >
              Cancel
            </Button>
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-xs text-brand-slate text-center">
              By unsubscribing, you will no longer receive team updates, schedules, 
              or important information from this team.
            </p>
          </div>
        </Card>
      </Container>
    </div>
  )
}

export default Unsubscribe
